import React from "react";
import ReactDOM from "react-dom/client";
import { isBefore, format } from "date-fns";
import CfcNoResult from "./CfcNoResult";

/**
 * @typedef {Object} AdditionalPropertiesCfcWodItem
 * @property {boolean} isPublished - Indique si le WOD est publié ou non
 */
/**
 * @typedef {import('../../models/CfcWodModel.js').CfcWodItem & AdditionalPropertiesCfcWodItem} CfcWodObj
 */
/** @typedef {import('../../models/CfcWodModel.js').CfcWodItem } CfcWodItem */
/** @typedef {import('../../models/CfcCategoryModel.js').CfcCategoryItem } CfcCategoryItem */

/**
 * @typedef {object} CfcWodsProps
 * @property {?string} cfcTournamentId - ID du tournoi pour lequel on veut récupérer les WODs
 * @property {Array<CfcWodItem>} cfcWods - Liste des WODs
 * @property {Array<CfcCategoryItem>} cfcCategories - Liste des catégories
 * @property {Array<CfcWodObj>} filteredCfcWods - Liste des WODs filtrés
 * @property {{ cfcCategoryId: ?number }} search - Objet contenant les valeurs de recherche
 */

class CfcWods extends React.Component {
  /**
   * @param {CfcWodsProps} props
   */
  constructor(props) {
    super(props);
    this.state = {
      cfcTournamentId: props.cfcTournamentId || null,
      cfcWods: /** @type {Array<CfcWodItem>} */ ([]),
      filteredCfcWods: /** @type {Array<CfcWodObj>} */ ([]),
      cfcCategories: /** @type {Array<CfcCategoryItem>} */ ([]),
      search: {
        cfcCategoryId: null
      }
    };
  }

  async componentDidMount() {
    const cfcWods = await this.fetchCfcWods();
    const cfcCategories = await this.fetchCfcCategories();

    this.setState(
      {
        search: {
          cfcCategoryId: cfcCategories.length ? cfcCategories[0].id : null
        }
      },
      () => {
        const filteredCfcWods = cfcWods
          .map(cfcWod => {
            cfcWod.isPublished = cfcWod.published_date && isBefore(new Date(cfcWod.published_date), new Date());
            return cfcWod;
          })
          .filter(cfcWod => {
            return this.state.search.cfcCategoryId ? cfcWod.cfcCategoryId === this.state.search.cfcCategoryId : true;
          });

        this.setState({ cfcWods, cfcCategories, filteredCfcWods });
      }
    );
  }

  /**
   * Permet de récupérer la liste des WODs d'une compétition
   * @returns {Promise<Array<CfcWodObj>>} - Liste des WODs
   */
  async fetchCfcWods() {
    var response = await fetch(`/api/cfc-wod/search?cfcTournamentId=${this.state.cfcTournamentId}`, {
      headers: {
        Authorization: "gestiaweb"
      }
    });

    return await response.json();
  }

  /**
   * Permet de récupérer la liste des catégories d'une compétition
   * @returns {Promise<Array<CfcCategoryItem>>} - Liste des catégories
   */
  async fetchCfcCategories() {
    var response = await fetch(`/api/cfc-category/search?cfcTournamentId=${this.state.cfcTournamentId}`, {
      headers: {
        Authorization: "gestiaweb"
      }
    });

    return await response.json();
  }

  /**
   * Lorsqu'on change la valeur du select, on filtre les inscriptions en fonction de la catégorie sélectionnée
   * @param {React.ChangeEvent<any>} event - Évenement associé au changement de valeur dans le select
   */
  onChangeOnSelect = event => {
    const cfcCategoryId = event.currentTarget.value ? parseInt(event.currentTarget.value, 10) : null;

    this.setState(
      {
        search: {
          ...this.state.search,
          cfcCategoryId
        }
      },
      () => {
        const filteredCfcWods = this.state.cfcWods
          .map(cfcWod => {
            cfcWod.isPublished = cfcWod.published_date && isBefore(new Date(cfcWod.published_date), new Date());
            return cfcWod;
          })
          .filter(cfcWod => {
            const matchesCategory = cfcCategoryId ? cfcWod.cfcCategoryId === cfcCategoryId : true;
            return matchesCategory;
          });

        this.setState({
          filteredCfcWods
        });
      }
    );
  };

  render() {
    return this.state.filteredCfcWods.length ? (
      <>
        <div className="flexline flexline--direction-column flexline--gap-16 flexline--no-mobile">
          <div>
            <select className="input-group__input input-group__input--with-small input-group__input--no-margin" onChange={this.onChangeOnSelect.bind(this)}>
              {this.state.cfcCategories.map((cfcCategory, index) => (
                <option key={index} value={cfcCategory.id}>
                  {cfcCategory.name}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid--has-2-child grid--16-gap">
            {this.state.filteredCfcWods.map((cfcWod, index) => (
              <>
                {cfcWod.isPublished ? (
                  <div>
                    <div key={index} className="uiblock uiblock--main uiblock--full-height">
                      <div className="uiblock__header uiblock__header--flex-space-between">
                        <h3 className="uiblock__title">
                          WOD {cfcWod.position} - {cfcWod.name}
                        </h3>
                        <div className="badge">{cfcWod.type}</div>
                      </div>
                      <div className="uiblock__content">
                        <div dangerouslySetInnerHTML={{ __html: cfcWod.description }} />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="background--light-grey borderradius--8 padding--16 flexline flexline--align-hz flexline--align-vz flexline--direction-column" key={index}>
                    <p className="paragraph paragraph--no-margin paragraph--bold-600">WOD {cfcWod.position} - Bientôt disponible</p>
                    <div className="space--8"></div>
                    <p className="paragraph paragraph--no-margin paragraph--grey-600">Annonce prévue le {format(new Date(cfcWod.published_date), "dd/MM/yyyy à HH:mm")}</p>
                  </div>
                )}
              </>
            ))}
          </div>
        </div>
      </>
    ) : (
      <CfcNoResult title="Aucun WOD trouvé" />
    );
  }
}

var cfcWodsNode = document.getElementById("cfc-wods");
if (cfcWodsNode) {
  var root = ReactDOM.createRoot(cfcWodsNode);
  root.render(<CfcWods cfcTournamentId={cfcWodsNode.getAttribute("data-cfctournamentid")} />);
}
